<template>
  <v-container class="mt-4">
    <v-row align="stretch">
      <v-col cols="12" md="4">
        <v-card height="100%" class="d-flex flex-column">
          <v-card-item>
            <template v-slot:prepend>
              <v-icon icon="mdi-credit-card-outline" size="large" color="primary" class="mr-3" />
            </template>
            <v-card-title>{{ $t("reports.generated.categories.payments") }}</v-card-title>
          </v-card-item>
          <v-divider />
          <v-card-text class="flex-grow-1">
            <p class="ma-0">{{ $t("reports.paymentsReport.description") }}</p>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn variant="flat" color="primary" @click="viewPaymentReports">
              {{ $t("reports.generated.view") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
      <v-col v-if="inventoryEnabled" cols="12" md="4">
        <v-card height="100%" class="d-flex flex-column">
          <v-card-item>
            <template v-slot:prepend>
              <v-icon icon="mdi-cart-outline" size="large" color="primary" class="mr-3" />
            </template>
            <v-card-title>{{ $t("reports.purchaseReport.title") }}</v-card-title>
          </v-card-item>
          <v-divider />

          <v-card-text class="flex-grow-1">
            <p class="ma-0">{{ $t("reports.purchaseReport.description") }}</p>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn variant="flat" color="primary" @click="viewPurchaseReports">
              {{ $t("reports.generated.view") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
      <v-col v-if="inventoryEnabled" cols="12" md="4">
        <v-card height="100%" class="d-flex flex-column">
          <v-card-item>
            <template v-slot:prepend>
              <v-icon icon="mdi-keyboard-return" size="large" color="primary" class="mr-3" />
            </template>
            <v-card-title>{{ $t("reports.returnsReport.title") }}</v-card-title>
          </v-card-item>
          <v-divider />

          <v-card-text class="flex-grow-1">
            <p class="ma-0">{{ $t("reports.returnsReport.description") }}</p>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn variant="flat" color="primary" @click="viewReturnReports">
              {{ $t("reports.generated.view") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
      <v-col v-if="inventoryEnabled" cols="12" md="4">
        <v-card height="100%" class="d-flex flex-column">
          <v-card-item>
            <template v-slot:prepend>
              <v-icon icon="mdi-warehouse" size="large" color="primary" class="mr-3" />
            </template>
            <v-card-title>{{ $t("reports.inventorySnapshot.title") }}</v-card-title>
          </v-card-item>
          <v-divider />

          <v-card-text class="flex-grow-1">
            <p class="ma-0">{{ $t("reports.inventorySnapshot.description") }}</p>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn
              data-test="view-inventory-snapshot-reports"
              variant="flat"
              color="primary"
              @click="viewInventorySnapshotReports"
            >
              {{ $t("reports.generated.view") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
      <v-col v-if="inventoryEnabled && isOwnerOrAdmin" cols="12" md="4">
        <v-card height="100%" class="d-flex flex-column">
          <v-card-item>
            <template v-slot:prepend>
              <v-icon icon="mdi-package-variant-closed" size="large" color="primary" class="mr-3" />
            </template>
            <v-card-title>{{ $t("reports.inventoryLog.title") }}</v-card-title>
          </v-card-item>
          <v-divider />

          <v-card-text class="flex-grow-1">
            <p class="ma-0">{{ $t("reports.inventoryLog.description") }}</p>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn variant="flat" color="primary" @click="navigateToInventoryLog">
              {{ $t("reports.generated.view") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
      <v-col v-if="isOwnerOrAdmin" cols="12" md="4">
        <v-card height="100%" class="d-flex flex-column">
          <v-card-item>
            <template v-slot:prepend>
              <v-icon icon="mdi-finance" size="large" color="primary" class="mr-3" />
            </template>
            <v-card-title>{{ $t("reports.stripeFinancials.title") }}</v-card-title>
          </v-card-item>
          <v-divider />

          <v-card-text class="flex-grow-1">
            <p class="ma-0">{{ $t("reports.stripeFinancials.description") }}</p>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn
              variant="flat"
              color="primary"
              append-icon="mdi-open-in-new"
              @click="openStripeReports"
            >
              {{ $t("reports.stripeFinancials.viewButton") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { useAccountStore } from "@/account/stores/account";
import { useAuthStore } from "@/auth/stores/auth";
import { useRouter } from "vue-router";
import { PlatformFeature } from "@/shared/enums/feature.enum";
import { hasFeatureAccess } from "@/shared/utils/feature.utils";

const router = useRouter();
const authStore = useAuthStore();
const accountId = computed(() => useAccountStore().getAccountId);
const inventoryEnabled = computed(() => hasFeatureAccess(PlatformFeature.INVENTORY));
const isOwnerOrAdmin = computed(() => authStore.isOwner || authStore.getRoleName === "admin");

const viewPaymentReports = () => {
  void router.push({
    name: "generatedReports",
    query: { type: ["payments_itemized", "payments_summary"] },
  });
};

const viewPurchaseReports = () => {
  void router.push({
    name: "generatedReports",
    query: { type: ["purchase_itemized", "purchase_summary"] },
  });
};

const viewReturnReports = () => {
  void router.push({
    name: "generatedReports",
    query: { type: ["return_itemized", "return_summary"] },
  });
};

const viewInventorySnapshotReports = () => {
  void router.push({
    name: "generatedReports",
    query: { type: ["inventory_snapshot"] },
  });
};

const openStripeReports = () => {
  window.open(`https://dashboard.stripe.com/${accountId.value}/reports/hub`, "_blank");
};

const navigateToInventoryLog = () => {
  void router.push("/inventory-log");
};
</script>
